import React, { useState, useEffect, useRef } from 'react'
import { Download, ChevronUp } from 'lucide-react'
import { isMobile } from 'react-device-detect'
import { AvatarType } from 'service/aiStudio'
import { ClothingData } from 'pages/ai-studio/tabs/FashionEditorTab/type/type'
import { convertStorageUrlToCdn } from 'utils/common'

// CSS 애니메이션 스타일
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`

interface PhotoSetStepProps {
  selectedAvatar: AvatarType | null
  clothingData: ClothingData
  photoSet?: {
    jobId: number
    backgroundName: string
    poseName: string
    status: string
    resultUrl: string
  }[]
  isPhotoSetGenerating?: boolean
  processingCount?: number
  onGeneratePhotoSet?: () => void
  onSelectedImageChange?: (imageUrl: string) => void
}

export const PhotoSetStep: React.FC<PhotoSetStepProps> = ({
  selectedAvatar,
  clothingData,
  photoSet = [],
  isPhotoSetGenerating = false,
  processingCount = 0,
  onGeneratePhotoSet,
  onSelectedImageChange,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)
  const thumbnailGridRef = useRef<HTMLDivElement>(null)
  const [isFullModalOpen, setIsFullModalOpen] = useState(false)
  // 애니메이션 스타일을 head에 추가
  useEffect(() => {
    // if (!document.getElementById('photoset-animations')) {
    //   const style = document.createElement('style')
    //   style.id = 'photoset-animations'
    //   style.textContent = animationStyles
    //   document.head.appendChild(style)
    // }
  }, [])

  // 이미지 선택 핸들러
  const handleImageSelection = (index: number) => {
    setSelectedImageIndex(index)
    if (onSelectedImageChange && photoSet[index]) {
      onSelectedImageChange(photoSet[index].resultUrl)
    }
  }
 const handleShowFullImage = () => {
    setIsFullModalOpen(true)
  }
  // 이미지가 생성되면 첫 번째 이미지를 선택
  useEffect(() => {
    if (photoSet.length > 0 && selectedImageIndex >= photoSet.length) {
      handleImageSelection(0)
    }
  }, [photoSet, selectedImageIndex])

  // PhotoSet 이미지가 처음 로드될 때 첫 번째 이미지를 선택
  useEffect(() => {
    if (photoSet.length > 0 && selectedImageIndex === 0) {
      if (onSelectedImageChange && photoSet[0].resultUrl) {
        onSelectedImageChange(photoSet[0].resultUrl)
      }
    }
  }, [photoSet.length])

  const handleScrollUp = () => {
    if (thumbnailGridRef.current) {
      const newPosition = Math.max(0, scrollPosition - 148) // 2줄 높이만큼 스크롤
      setScrollPosition(newPosition)
      thumbnailGridRef.current.scrollTop = newPosition
    }
  }

  const handleScrollDown = () => {
    if (thumbnailGridRef.current) {
      const maxScroll =
        thumbnailGridRef.current.scrollHeight -
        thumbnailGridRef.current.clientHeight
      const newPosition = Math.min(maxScroll, scrollPosition + 148)
      setScrollPosition(newPosition)
      thumbnailGridRef.current.scrollTop = newPosition
    }
  }

  const handleDownloadImage = async (imageUrl: string, index: number) => {
    try {
      // 이미지를 fetch해서 blob으로 변환
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD 형태
      const time = new Date().toLocaleTimeString()
      // blob을 URL로 변환
      const blobUrl = window.URL.createObjectURL(blob)

      // 다운로드 링크 생성 및 클릭
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `style_shot_${today}_${time}.jpg`
      document.body.appendChild(link)
      link.click()

      // 메모리 정리
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Download failed:', error)
      // fallback: 직접 링크 열기
      window.open(imageUrl, '_blank')
    }
  }

  const handleDownloadAll = () => {
    photoSet.forEach((el, index) => {
      setTimeout(() => {
        handleDownloadImage(el.resultUrl, index)
      }, index * 200) // 200ms 간격으로 다운로드
    })
  }

  if (isMobile) {
    // 모바일 전용 레이아웃
    return (
      <div className="flex flex-col gap-4 h-full overflow-y-auto">
        {/* 제목과 설명 섹션 */}
        <div className="flex flex-col gap-1">
          <h3 className="text-[#2E343D] text-[18px font-bold">
            스타일 샷 세트 생성
          </h3>
          <p className="text-[#2E343D] text-base">
            다양한 컨셉과 포즈로 만들어진 스타일 샷 세트를 받아보세요!
          </p>
        </div>

        {/* 메인 컨텐츠 영역 */}
        <div className=" flex justify-between gap-[10px]">
          {/* 왼쪽 사진 확대  */}
          {isPhotoSetGenerating && photoSet.length === 0 ? (
            <>
              {/* 좌측 - 큰 이미지 스켈레톤 */}
              <div className="w-[198px] h-[351px] rounded-[10px] overflow-hidden relative bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
                <div className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-skeleton"></div>
              </div>
            </>
          ) : (
            <div className="relative w-[198px] h-[351px] rounded-[10px] overflow-hidden bg-gray-200">
              {photoSet[selectedImageIndex] && (
                <img
                  src={photoSet[selectedImageIndex].resultUrl}
                  alt="선택된 스타일 샷"
                  className="w-full h-full object-cover"
                />
              )}

              {/* 다운로드 버튼 오버레이 */}
              <button
                onClick={() =>
                  handleDownloadImage(
                    photoSet[selectedImageIndex].resultUrl,
                    selectedImageIndex
                  )
                }
                className="absolute bottom-3 right-3 w-10 h-10 bg-black bg-opacity-60 border border-white border-opacity-20 rounded-[6px] flex items-center justify-center hover:bg-opacity-90 transition-opacity"
              >
                <Download size={16} className="text-white" />
              </button>
            </div>
          )}

          <>
            {/* 우측 - 썸네일 그리드 */}
            <div className="w-[135px] max-h-[428px] overflow-auto ">
              <div className="grid grid-cols-2 gap-1 h-full">
                {/* 완료된 이미지들 렌더링 (모바일은 최대 6개) */}
                {photoSet.map((el, index) => (
                  <div
                    key={`completed_mobile_${index}`}
                    className={`w-[65px] h-[115px] rounded-[8px] cursor-pointer transition-all duration-300 ${
                      selectedImageIndex === index
                        ? 'border-[3px] border-[#00B7B9]'
                        : 'border border-transparent'
                    }`}
                    onClick={() => handleImageSelection(index)}
                    style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.2}s both`,
                    }}
                  >
                    <img
                      src={el.resultUrl}
                      alt={`스타일 샷 ${index + 1}`}
                      className="w-full h-full object-cover rounded-[4px]"
                    />
                    
                  </div>
                ))}

                {/* 처리 중인 이미지들의 스켈레톤 UI (모바일용) */}
                {Array.from(
                  { length: Math.min(processingCount, 6 - photoSet.length) },
                  (_, index) => (
                    <div
                      key={`processing_mobile_${index}`}
                      className="w-[65px] h-[115px] rounded-[10px] overflow-hidden bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400 relative"
                    >
                      <div className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-skeleton"></div>
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        </div>
      </div>
    )
  }

  // 데스크톱 레이아웃 (기존)
  return (
    <div className="flex flex-col gap-4 h-full">
      {/* 제목과 설명 섹션 */}
      <div className="flex flex-col justify-center gap-2 w-[578px]">
        <h3 className="text-[#2E343D] text-[18px] font-bold">
          스타일 샷 세트 생성
        </h3>
        <p className="text-[#2E343D] text-base">
          다양한 컨셉과 포즈로 만들어진 스타일 샷 세트를 받아보세요!
        </p>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div className="flex-1 bg-[#F6F8FB] rounded-[10px] p-4 flex justify-between gap-4">
        {isPhotoSetGenerating && photoSet.length === 0? (
          <div className="relative w-[243px] h-[428px] rounded-[10px] overflow-hidden bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400 shrink-0">
            <div className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-skeleton"></div>
          </div>
        ) : (
          <>
            <div className="relative w-[243px] h-[428px] rounded-[10px] overflow-hidden shrink-0">
              {photoSet[selectedImageIndex] && (
                <img
                  src={convertStorageUrlToCdn(
                    photoSet[selectedImageIndex].resultUrl
                  )}
                  alt="선택된 스타일 샷"
                  className="w-full h-full object-cover"
                />
              )}

              {/* 다운로드 버튼 오버레이 */}
              <button
                onClick={() =>
                  handleDownloadImage(
                    photoSet[selectedImageIndex].resultUrl,
                    selectedImageIndex
                  )
                }
                className="absolute bottom-3 right-3 w-10 h-10 bg-black bg-opacity-60 border border-white border-opacity-20 rounded-md flex items-center justify-center hover:bg-opacity-90 transition-opacity"
              >
                <Download size={16} className="text-white" />
              </button>
            
            </div>
          </>
        )}

        {/* 우측 - 썸네일 그리드와 스크롤 컨트롤 */}
        <div className="flex flex-col w-full h-[428px]">
          {/* 상단 스크롤 버튼 */}
          <button
            onClick={handleScrollUp}
            className="w-[86px] h-[22px] bg-black bg-opacity-60 border border-white border-opacity-20 rounded-md flex items-center justify-center self-center hover:bg-opacity-90 transition-opacity"
          >
            <ChevronUp size={24} className="text-white" />
          </button>

          {/* 썸네일 그리드 */}
          <div
            ref={thumbnailGridRef}
            className="flex-1 overflow-auto py-2"
            style={{ scrollBehavior: 'smooth' }}
          >
            <div className="grid grid-cols-4 gap-1 ">
              {/* 완료된 이미지들 렌더링 */}
              {photoSet.map((el, index) => (
                <div
                  key={`completed_${index}`}
                  className={`w-[65px] h-[115px] rounded-[10px] overflow-hidden cursor-pointer transition-all duration-300 ${
                    selectedImageIndex === index
                      ? 'border-[3px] border-[#00B7B9]'
                      : 'border border-transparent'
                  }`}
                  onClick={() => handleImageSelection(index)}
                >
                  <img
                    src={convertStorageUrlToCdn(el.resultUrl)}
                    alt={`스타일 샷 ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}

              {/* 처리 중인 이미지들의 스켈레톤 UI */}
              {Array.from({ length: processingCount }, (_, index) => (
                <div
                  key={`processing_${index}`}
                  className="w-[65px] h-[115px] rounded-[10px] overflow-hidden bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400 relative"
                >
                  <div className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-skeleton"></div>
                </div>
              ))}
            </div>
          </div>

          {/* 하단 스크롤 버튼 */}
          <button
            onClick={handleScrollDown}
            className="w-[86px] h-[22px] bg-black bg-opacity-60 border border-white border-opacity-20 rounded-md flex items-center justify-center self-center hover:bg-opacity-90 transition-opacity"
            style={{ transform: 'rotate(180deg)' }}
          >
            <ChevronUp size={24} className="text-white" />
          </button>
        </div>
      </div>
    </div>
  )
}
